package uz.mint.frontend.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.events.Event
import org.w3c.dom.url.URLSearchParams
import kotlinx.browser.window
import uz.mint.frontend.EyebrowStyle
import uz.mint.frontend.PageGutter
import uz.mint.frontend.ShellStyle
import uz.mint.frontend.alignItems
import uz.mint.frontend.api.CatalogState
import uz.mint.frontend.api.Destination
import uz.mint.frontend.api.DestinationKind
import uz.mint.frontend.api.DestinationState
import uz.mint.frontend.api.Destinations
import uz.mint.frontend.api.Plan
import uz.mint.frontend.api.encodeURIComponent
import uz.mint.frontend.api.PlanPage
import uz.mint.frontend.api.flag
import uz.mint.frontend.api.pathIn
import uz.mint.frontend.api.rememberDestinations
import uz.mint.frontend.api.rememberPlanPage
import uz.mint.frontend.components.widgets.DestinationGrid
import uz.mint.frontend.components.widgets.PlanError
import uz.mint.frontend.components.widgets.PlanGrid
import uz.mint.frontend.components.widgets.PlanNotice
import uz.mint.frontend.components.widgets.SearchBox
import uz.mint.frontend.flexWrapCss
import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.PrivacyPolicy
import uz.mint.frontend.i18n.PublicOffer
import uz.mint.frontend.i18n.SiteStrings
import uz.mint.frontend.i18n.stringsFor
import uz.mint.frontend.justifyContent
import uz.mint.frontend.toSitePalette
import uz.mint.frontend.widthCss

/**
 * The catalogue at `/plans`, `/uz/plans` and `/en/plans` — **in two steps: destination, then
 * package.**
 *
 * Step one is a picker over every country and bundle we sell; step two is that destination's
 * packages. It replaced a flat listing of 2 900 plans filtered by three category tabs, which asked
 * the customer to classify their own trip before they could see a price and then made them page
 * through 121 pages of everywhere on earth to find Türkiye.
 *
 * **Both steps live at the same route, and the choice lives in the query string** —
 * `/plans?country=TR`, `/plans?region=Europe%20(30%2B%20areas)`. So a destination is linkable,
 * bookmarkable and reachable from the landing page's cards, and the browser's back button steps
 * out of a destination rather than off the page.
 *
 * Search is split between the steps on purpose: destinations are filtered **in the browser**,
 * because the whole index is one small response already loaded, while plans are filtered and paged
 * **in the database**, because there are thousands of them. That is the same rule as before, just
 * applied to two different lists.
 */
@Composable
fun AllPlansPage(locale: Locale) {
    val strings = stringsFor(locale)

    // The destination the customer is on, or null for the picker. Seeded from the URL so a shared
    // link opens where it says it does.
    var chosen by remember { mutableStateOf(destinationKeyFromUrl()) }
    var page by remember { mutableStateOf(pageFromUrl()) }
    var query by remember { mutableStateOf("") }
    var selected by remember { mutableStateOf<Plan?>(null) }
    var legal by remember { mutableStateOf<LegalDoc?>(null) }

    val (destinationState, retryDestinations) = rememberDestinations(locale)

    // Resolved for its localised name and plan count; the *fetch* does not wait for it, because
    // the query parameter is all the backend needs. Until the index lands a country card's heading
    // reads "TR" rather than "Турция" — a region's id is already its name, so only countries flash,
    // and only for as long as one small request takes.
    val active = chosen?.let { key ->
        (destinationState as? DestinationState.Loaded)?.destinations?.all
            ?.firstOrNull { it.kind == key.kind && it.id == key.value }
            ?: key.provisional()
    }

    // Never on the first composition, only on an actual navigation. The static export renders this
    // page in a headless browser and snapshots the DOM; a `pushState` while that is happening
    // counts as navigation, the fetch behind the grid is abandoned, and the export captures a
    // catalogue with no cards in it — which is exactly what the first version of this did, on all
    // three locales at once, while the landing page went on working.
    var firstRender by remember { mutableStateOf(true) }
    LaunchedEffect(chosen, page) {
        if (firstRender) firstRender = false else syncUrl(locale.plansPath, chosen, page)
    }

    // Back and Forward move through the entries `syncUrl` pushed, and without this they would move
    // the address bar while leaving the screen on whatever was already there — the URL saying
    // `/plans` and the page still showing Türkiye's packages. `pushState` does not fire `popstate`
    // itself, so this only ever reacts to the customer, and `syncUrl` no-ops when the URL already
    // agrees, so reading the state back cannot push a duplicate entry.
    DisposableEffect(Unit) {
        val onPopState: (Event) -> Unit = {
            chosen = destinationKeyFromUrl()
            page = pageFromUrl()
        }
        window.addEventListener("popstate", onPopState)
        onDispose { window.removeEventListener("popstate", onPopState) }
    }

    Column(Modifier.fillMaxWidth()) {
        // The landing sections are not on this page, so the nav's anchors have to reach back to
        // it, and the language menu has to stay on the catalogue rather than dumping you home.
        TopBar(strings)
        NavHeader(
            locale = locale,
            strings = strings,
            anchorBase = locale.homePath,
            localePath = { it.plansPath },
        )

        Div(
            ShellStyle.toModifier()
                // Repeats the gutter deliberately — see the note in Tariffs: this is emitted as
                // the `padding` shorthand and would otherwise erase ShellStyle's horizontal
                // padding on a phone.
                .padding(top = clamp(32.px, 3.4.vw, 56.px), bottom = clamp(48.px, 5.vw, 84.px), leftRight = PageGutter)
                .toAttrs()
        ) {
            if (active == null) {
                PickerStep(
                    locale = locale,
                    strings = strings,
                    state = destinationState,
                    query = query,
                    onQuery = { query = it },
                    onRetry = retryDestinations,
                    onPick = {
                        chosen = DestinationKey(it.kind, it.id)
                        page = 0
                    },
                )
            } else {
                PackagesStep(
                    locale = locale,
                    strings = strings,
                    destination = active,
                    page = page,
                    onBack = {
                        chosen = null
                        page = 0
                        // The picker's own search starts clean; the previous query was about
                        // whichever destination they have just left.
                        query = ""
                    },
                    onPage = { page = it },
                    onSelectPlan = { selected = it },
                )
            }
        }

        Footer(
            locale = locale,
            strings = strings,
        )
    }

    selected?.let { plan ->
        CheckoutDialog(locale, strings, plan) { selected = null }
    }

    when (legal) {
        LegalDoc.OFFER -> LegalOverlay(strings.legalOffer, PublicOffer, strings) { legal = null }
        LegalDoc.PRIVACY -> LegalOverlay(strings.legalPrivacy, PrivacyPolicy, strings) { legal = null }
        null -> Unit
    }
}

// --- Step one: where are you going -----------------------------------------------------------

@Composable
private fun PickerStep(
    locale: Locale,
    strings: SiteStrings,
    state: DestinationState,
    query: String,
    onQuery: (String) -> Unit,
    onRetry: () -> Unit,
    onPick: (Destination) -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()

    Column(Modifier.fillMaxWidth().gap(14.px).margin(bottom = clamp(20.px, 2.2.vw, 30.px))) {
        Link(
            path = locale.homePath,
            modifier = EyebrowStyle.toModifier()
                .fontSize(12.px).letterSpacing(0.1.em).color(palette.accentText),
            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
        ) { SpanText("← " + strings.plansBack) }

        // [SiteStrings.plansTitle], not the landing section's "Куда вы едете?": this page has its
        // own `<title>` and meta description aimed at "все тарифы eSIM", and an H1 that asks a
        // question instead of naming the page throws that away. The question is the lede below.
        H1(
            Modifier.fontSize(clamp(32.px, 4.vw, 56.px)).lineHeight(1.02)
                .letterSpacing((-0.035).em).fontWeight(FontWeight.ExtraBold).toAttrs()
        ) { SpanText(strings.plansTitle) }

        P(Modifier.fontSize(16.px).lineHeight(1.5).color(palette.ink2).maxWidth(44.em).toAttrs()) {
            SpanText(strings.plansLede)
        }
    }

    Div(Modifier.fillMaxWidth().margin(bottom = 22.px).toAttrs()) {
        SearchBox(strings.destSearchPlaceholder, query, maxWidth = "min(100%, 420px)", onQuery = onQuery)
    }

    when (state) {
        is DestinationState.Loading -> PlanNotice(strings.destLoading)

        is DestinationState.Failed -> DestinationError(strings, onRetry)

        is DestinationState.Loaded -> {
            val filtered = state.destinations.search(query)
            if (filtered.isEmpty) {
                PlanNotice(strings.destNoMatch)
            } else {
                DestinationBlocks(filtered, locale, strings, onPick)
            }
        }
    }
}

/**
 * Countries first, multi-country bundles after.
 *
 * Two blocks rather than one list because they answer different questions: a country is a place
 * you are going, a bundle is a way to cover several. Mixing them alphabetically would file
 * "Europe (30+ areas)" between Estonia and Finland, where it reads as a country.
 */
@Composable
private fun DestinationBlocks(
    destinations: Destinations,
    locale: Locale,
    strings: SiteStrings,
    onPick: (Destination) -> Unit,
) {
    if (destinations.countries.isNotEmpty()) {
        SectionLabel(strings.destAllCountries)
        DestinationGrid(
            destinations = destinations.countries,
            locale = locale,
            strings = strings,
            href = { it.pathIn(locale) },
            onSelect = onPick,
        )
    }

    if (destinations.regions.isNotEmpty()) {
        SectionLabel(strings.destRegions, topGap = destinations.countries.isNotEmpty())
        DestinationGrid(
            destinations = destinations.regions,
            locale = locale,
            strings = strings,
            href = { it.pathIn(locale) },
            onSelect = onPick,
        )
    }
}

// --- Step two: which package -----------------------------------------------------------------

@Composable
private fun PackagesStep(
    locale: Locale,
    strings: SiteStrings,
    destination: Destination,
    page: Int,
    onBack: () -> Unit,
    onPage: (Int) -> Unit,
    onSelectPlan: (Plan) -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()

    // The step owns its request, the way every other section here does. Hoisting it to the page
    // would fire a full unfiltered catalogue query behind the picker as well — 24 plans nobody
    // ever sees, and one more fetch competing with the destination index during a static export.
    val (state, onRetry) = rememberPlanPage(
        locale = locale,
        page = page,
        size = PAGE_SIZE,
        destination = destination,
    )

    Column(Modifier.fillMaxWidth().gap(14.px).margin(bottom = clamp(20.px, 2.2.vw, 30.px))) {
        // A real link back to the bare catalogue, click-intercepted so it steps back without a
        // page load. Same reasoning as the destination cards themselves.
        A(
            href = locale.plansPath,
            attrs = EyebrowStyle.toModifier()
                .fontSize(12.px).letterSpacing(0.1.em).color(palette.accentText)
                .textDecorationLine(TextDecorationLine.None)
                .cursor(Cursor.Pointer)
                .toAttrs {
                    onClick { event ->
                        if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
                            event.preventDefault()
                            onBack()
                        }
                    }
                }
        ) { SpanText("← " + strings.destBack) }

        Row(Modifier.fillMaxWidth().gap(16.px).flexWrapCss(), verticalAlignment = Alignment.CenterVertically) {
            SpanText(destination.flag(), Modifier.fontSize(clamp(34.px, 4.vw, 46.px)).lineHeight(1))
            H1(
                Modifier.fontSize(clamp(28.px, 3.6.vw, 48.px)).lineHeight(1.05)
                    .letterSpacing((-0.035).em).fontWeight(FontWeight.ExtraBold).toAttrs()
            ) { SpanText(destination.name) }
        }

        P(Modifier.fontSize(16.px).lineHeight(1.5).color(palette.ink2).maxWidth(44.em).toAttrs()) {
            // The live count for this destination, straight from the response that filled the grid.
            val total = (state as? CatalogState.Loaded)?.page?.total
            SpanText(if (total == null) strings.tariffsSub else strings.plansCount(locale, total))
        }
    }

    when (state) {
        is CatalogState.Loading -> PlanNotice(strings.tariffsLoading)

        is CatalogState.Failed -> PlanError(strings, onRetry)

        is CatalogState.Loaded -> if (state.page.items.isEmpty()) {
            PlanNotice(strings.tariffsEmpty)
        } else {
            PlanGrid(state.page.items, locale, strings, state.refreshing, onSelectPlan)
            Pager(strings, state.page, locale.plansPath, destination, onPage)
        }
    }
}

// --- Shared bits ------------------------------------------------------------------------------

@Composable
private fun SectionLabel(text: String, topGap: Boolean = false) {
    val palette = ColorMode.current.toSitePalette()
    SpanText(
        text,
        EyebrowStyle.toModifier()
            .display(DisplayStyle.Block)
            .fontSize(12.px).letterSpacing(0.1.em).color(palette.accentText)
            .margin(top = if (topGap) 34.px else 0.px, bottom = 12.px),
    )
}

/**
 * Previous / next plus a windowed strip of page numbers.
 *
 * Every entry is a real `<a href>` carrying the destination, so page 3 of Türkiye has an address
 * that can be linked, bookmarked or opened in a new tab. The click is intercepted so paging stays
 * instant; the href exists for everything that is not a plain left click.
 */
@Composable
private fun Pager(
    strings: SiteStrings,
    page: PlanPage,
    basePath: String,
    destination: Destination,
    onPage: (Int) -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()
    val key = DestinationKey(destination.kind, destination.id)

    if (page.totalPages <= 1) return

    Row(
        Modifier.fillMaxWidth().gap(8.px).flexWrapCss().margin(top = 24.px)
            .justifyContent("center")
            .alignItems("center"),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        if (page.hasPrevious) {
            PageLink("←", buildPath(basePath, key, page.page - 1), page.page - 1, false, palette.rule, palette.ink, onPage)
        }

        windowedPages(page.page, page.totalPages).forEach { entry ->
            when (entry) {
                null -> SpanText("…", Modifier.fontSize(14.px).color(palette.ink2).padding(leftRight = 4.px))
                else -> PageLink(
                    label = (entry + 1).toString(),
                    href = buildPath(basePath, key, entry),
                    target = entry,
                    current = entry == page.page,
                    rule = palette.rule,
                    ink = palette.ink,
                    onPage = onPage,
                )
            }
        }

        if (page.hasNext) {
            PageLink("→", buildPath(basePath, key, page.page + 1), page.page + 1, false, palette.rule, palette.ink, onPage)
        }
    }

    SpanText(
        strings.plansPageOf(page.page + 1, page.totalPages),
        Modifier.fillMaxWidth().margin(top = 10.px).fontSize(13.px)
            .color(palette.ink2).opacity(0.75).textAlign(TextAlign.Center),
    )
}

@Composable
private fun PageLink(
    label: String,
    href: String,
    target: Int,
    current: Boolean,
    rule: com.varabyte.kobweb.compose.ui.graphics.Color,
    ink: com.varabyte.kobweb.compose.ui.graphics.Color,
    onPage: (Int) -> Unit,
) {
    A(
        href = href,
        attrs = Modifier
            .padding(topBottom = 9.px, leftRight = 13.px)
            .fontWeight(FontWeight.Bold).fontSize(14.px)
            .border(2.px, LineStyle.Solid, rule)
            .backgroundColor(if (current) rule else Colors.Transparent)
            .color(if (current) Colors.White else ink)
            .textDecorationLine(TextDecorationLine.None)
            .cursor(Cursor.Pointer)
            .toAttrs {
                onClick { event ->
                    // Plain left click pages in place; ctrl/cmd-click and middle click are left
                    // to the browser, which is the behaviour the href is there to preserve.
                    if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
                        event.preventDefault()
                        onPage(target)
                    }
                }
            }
    ) { SpanText(label) }
}

/**
 * The page numbers worth showing: first, last, and a window around the current one.
 *
 * `null` is an ellipsis. A strip that reflows every time the current page changes width is worse
 * than one that keeps its shape.
 */
private fun windowedPages(current: Int, total: Int): List<Int?> {
    if (total <= 7) return (0 until total).toList()

    val around = (current - 1..current + 1).filter { it in 1 until total - 1 }
    val pages = (listOf(0) + around + listOf(total - 1)).distinct().sorted()

    return buildList {
        pages.forEachIndexed { i, p ->
            if (i > 0 && p - pages[i - 1] > 1) add(null)
            add(p)
        }
    }
}

// --- URL state --------------------------------------------------------------------------------

/**
 * The chosen destination as it travels in the query string.
 *
 * Kept as a plain key rather than a [Destination] because it is read from the URL before the
 * destination index has loaded — and everything the catalogue request needs is these two fields.
 */
private data class DestinationKey(val kind: DestinationKind, val value: String) {
    /**
     * A stand-in until the real one arrives, so step two can start fetching immediately instead of
     * waiting on a second request just to learn a display name.
     */
    fun provisional() = Destination(
        id = value,
        kind = kind,
        name = value,
        planCount = 0,
        fromPriceUzs = null,
        popular = false,
        popularRank = null,
        global = false,
    )
}

/**
 * 24 rather than the API default of 12: this page is for browsing, and a full screen of cards is
 * fewer page turns for the same payload size the landing page would have wasted.
 */
private const val PAGE_SIZE = 24

/** `?country=TR` or `?region=…`; anything else means the picker. */
private fun destinationKeyFromUrl(): DestinationKey? {
    val params = URLSearchParams(window.location.search)
    params.get("country")?.trim()?.takeIf { it.isNotEmpty() }
        ?.let { return DestinationKey(DestinationKind.COUNTRY, it.uppercase()) }
    params.get("region")?.trim()?.takeIf { it.isNotEmpty() }
        ?.let { return DestinationKey(DestinationKind.REGION, it) }
    return null
}

/**
 * The page the address bar asks for, counting from 1 and clamped to something sane.
 *
 * Read straight from `window.location` rather than through the router: the value is needed once,
 * at first composition, and a junk `?page=abc` should land on the first page rather than break the
 * screen.
 */
private fun pageFromUrl(): Int =
    URLSearchParams(window.location.search).get("page")?.toIntOrNull()
        ?.let { (it - 1).coerceIn(0, 999) }
        ?: 0

/** `/plans`, `/plans?country=TR`, `/plans?country=TR&page=3`. Page 1 needs no parameter. */
private fun buildPath(basePath: String, key: DestinationKey?, page: Int): String {
    val params = buildList {
        key?.let {
            val name = if (it.kind == DestinationKind.COUNTRY) "country" else "region"
            add("$name=${encodeURIComponent(it.value)}")
        }
        if (page > 0) add("page=${page + 1}")
    }
    return if (params.isEmpty()) basePath else basePath + "?" + params.joinToString("&")
}

/**
 * Keeps the address bar in step with what is on screen, so it can be copied or bookmarked.
 *
 * `pushState`, not `replaceState`: choosing a destination and turning a page are both navigation,
 * and the back button should undo them one at a time. Skipped when the URL already says this, or
 * the first render would push a duplicate entry and the first press of Back would appear to do
 * nothing.
 */
private fun syncUrl(basePath: String, key: DestinationKey?, page: Int) {
    val target = buildPath(basePath, key, page)
    if (window.location.pathname + window.location.search != target) {
        window.history.pushState(null, "", target)
    }
}
